// Zentriertes Sheet über der Seite (Handoff Selbstauskunft §3): 480 px, Radius 30, Fläche --sheet,
// abgedunkelter + weichgezeichneter Hintergrund. Kopf mit optionalem Zurück, App-Icon, Titel, Schließen.
// Esc schließt (außer ein Bestätigungsdialog liegt darüber), die Seite darunter scrollt nicht.
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CaretLeft, X, type Icon } from '@phosphor-icons/react';
import { useDialogFokus } from '../immobilien/ui/fokus';
import { useMobilModus } from '../../lib/immobilien/mobil';

export interface SheetProps {
  titel: string;
  icon: Icon;
  /** Hintergrund des App-Icons */
  iconFarbe: string;
  onSchliessen: () => void;
  /** Zurück-Pfeil links im Kopf (z. B. Editor → Liste) */
  onZurueck?: () => void;
  zurueckTip?: string;
  /** Tooltip des Schließen-Knopfs (Standard „Schließen“) */
  schliessenTip?: string;
  breite?: number;
  children: ReactNode;
}


export function Sheet({ titel, icon: I, iconFarbe, onSchliessen, onZurueck, zurueckTip = 'Zur Liste', schliessenTip = 'Schließen', breite = 480, children }: SheetProps) {
  const fenster = useDialogFokus<HTMLDivElement>();
  // Handy: Tippflächen 44 px
  const { mobil } = useMobilModus();
  const RUND = { width: mobil ? 44 : 34, height: mobil ? 44 : 34, borderRadius: '50%', border: 'none', cursor: 'pointer', background: 'var(--fill2)', color: 'var(--ink)' } as const;

  useEffect(() => {
    const esc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Ein Bestätigungsdialog über dem Sheet schließt zuerst sich selbst.
      if (document.querySelector('[role="alertdialog"]')) return;
      onSchliessen();
    };
    window.addEventListener('keydown', esc);
    return () => window.removeEventListener('keydown', esc);
  }, [onSchliessen]);

  // Seite darunter scrollt nicht, solange das Sheet offen ist.
  useEffect(() => {
    const html = document.documentElement;
    const vorher = html.style.overflow;
    html.style.overflow = 'hidden';
    return () => { html.style.overflow = vorher; };
  }, []);

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center" style={{ zIndex: 400, padding: 10, fontFamily: "'Manrope', -apple-system, sans-serif" }}>
      <div aria-hidden onMouseDown={onSchliessen} className="absolute inset-0"
        style={{ background: 'rgba(10,18,30,0.38)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)' }} />
      <div ref={fenster} tabIndex={-1} role="dialog" aria-modal="true" aria-label={titel}
        className="immo-sheet immo-scroll immo-side-in relative flex w-full flex-col overflow-y-auto outline-none"
        style={{ maxWidth: `min(${breite}px, calc(100vw - 20px))`, maxHeight: 'calc(100dvh - 40px)', borderRadius: 30, background: 'var(--sheet)', color: 'var(--ink)',
          boxShadow: '0 40px 80px -24px rgba(0,0,0,0.55), inset 0 0 0 1px var(--line)', overscrollBehavior: 'contain' }}>
        <div className="sticky top-0 flex items-center" style={{ zIndex: 2, gap: 10, padding: '16px 18px 12px', background: 'var(--sheet)' }}>
          {onZurueck && (
            <button type="button" onClick={onZurueck} aria-label={zurueckTip} data-tip={zurueckTip} className="inline-flex shrink-0 items-center justify-center" style={RUND}>
              <CaretLeft size={14} weight="bold" />
            </button>
          )}
          <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 11, background: iconFarbe, color: '#fff' }}>
            <I size={18} weight="fill" />
          </span>
          <h2 className="m-0 min-w-0 flex-1 truncate" style={{ fontSize: 18, fontWeight: 800, letterSpacing: '-0.01em' }}>{titel}</h2>
          <button type="button" onClick={onSchliessen} aria-label="Schließen" data-tip={schliessenTip} className="inline-flex shrink-0 items-center justify-center" style={RUND}>
            <X size={14} weight="bold" />
          </button>
        </div>
        <div className="flex flex-col" style={{ padding: '4px 18px 20px', gap: 12 }}>{children}</div>
      </div>
    </div>,
    document.body,
  );
}
